import { useCallback, useMemo, useRef } from 'react'
import useSWR, { SWRConfiguration } from 'swr'

import { Api } from '@lib/api'
import { toSearchParams } from '@utils/object'
import { ApiGetResponse, DataParams, MetadataResponse } from 'types/api'
import { ArrayOrSingle } from 'types/common'

export type DataHookOptions<
  Data,
  Params extends DataParams<ArrayOrSingle<Data>> = DataParams<
    ArrayOrSingle<Data>
  >,
> = {
  params?: Params
  pause?: boolean
  config?: SWRConfiguration<Data>
}

export function useData<
  D,
  P extends DataParams<ArrayOrSingle<D>> = DataParams<ArrayOrSingle<D>>,
  A extends Api<any> = Api<any>,
>(
  api: A,
  path: string | null,
  { params, pause, config }: DataHookOptions<D, P> = {}
) {
  const initialLoad = useRef<boolean>(true)
  const paramsString = useMemo(() => JSON.stringify(params), [params])

  const key = useCallback(() => {
    if (pause || !path) return null
    return [path, paramsString ? JSON.parse(paramsString) : {}] as [string, P]
  }, [paramsString, path, pause])

  const _fetcher = useCallback(
    async (currentKey: [any, ...unknown[]]) => {
      const [curPath, curParams] = currentKey as [string, P]

      const response = await api.get<D>(
        curPath,
        toSearchParams(curParams as object)
      )

      return response
    },
    [api]
  )

  const { data, error, isLoading, isValidating, mutate } = useSWR<D>(
    key,
    _fetcher,
    config
  )
  const loading =
    initialLoad.current && config?.fallbackData ? false : isLoading
  if (isLoading && initialLoad.current) {
    initialLoad.current = false
  }

  return {
    data,
    error,
    isLoading: loading,
    isValidating,
    mutate,
  }
}

// Wrapper to add list specific logic
export function useDataList<
  D,
  P extends DataParams<ArrayOrSingle<D>> = DataParams<ArrayOrSingle<D>>,
  A extends Api<any> = Api<any>,
>(
  api: A,
  path: string | null,
  { params, pause, config }: DataHookOptions<D[], P> = {}
) {
  const { data, error, isLoading, ...swrResponse } = useData<
    { data: D[]; count?: number },
    P,
    A
  >(api, path, {
    params,
    pause,
    config: config as SWRConfiguration<{ data: D[]; count?: number }>,
  })
  const metadataCasted = data as MetadataResponse<D[]> | undefined
  const straightCasted = data as D[] | undefined

  return {
    ...swrResponse,
    data:
      metadataCasted?.data !== undefined ? metadataCasted.data : straightCasted,
    count: data?.count ?? data?.data?.length,
    loading: isLoading,
    error,
  }
}

// Wrapper to add read specific logic
export function useDataRead<
  D,
  P extends DataParams<D> = DataParams<D>,
  A extends Api<any> = Api<any>,
>(
  api: A,
  path: string | null,
  { params, pause, config }: DataHookOptions<D, P> = {}
) {
  const { data, error, isLoading, mutate, ...swrResponse } = useData<
    ApiGetResponse<D>,
    P,
    A
  >(api, path, {
    params,
    pause,
    config: config as SWRConfiguration<ApiGetResponse<D>>,
  })

  const metadataCasted = data as MetadataResponse<D> | undefined
  const straightCasted = data as D | undefined

  return {
    ...swrResponse,
    data:
      metadataCasted?.data !== undefined ? metadataCasted.data : straightCasted,
    loading: isLoading,
    error,
    mutate,
  }
}
